import { isEmpty } from "lodash-es";

import {
  BaseEntityDataDto,
  BaseEntityDto,
  BaseEntityFullRefDto,
  BaseEntityRefDto,
  EntityDataOriginMode,
  EntityFormType,
  IAssetSourceEntityDto,
  IHasGlobalNumberDto,
  IHasLocalNumberDto,
  IMightHaveDataGrantsBriefDto,
  IMightHaveDataGrantsDto,
  IMightHaveDepartmentsDto,
  IMightHaveImportMetaDto,
  IMightHaveLocationsDto,
  IMightHaveOriginDto,
  IMightHavePoolsDto,
  IMightHaveTagsDto,
  IMightHaveTenantDto,
  IMightHaveTenantRequestsDto,
  IMightHaveUnseenInfoDto,
  TenantDto,
} from "@/core/api/generated/v1";
import store from "@/store";

import {
  BaseEntityDataDtoAll,
  BaseEntityDataDtoAny,
  BaseEntityDefaultValues,
  BaseEntityDtoAll,
  BaseEntityDtoAny,
  EntityAffiliationInfo,
  EntityDataMeta,
  EntityDefaultValuesMatchStatus,
  EntityDefaultValuesMeta,
  EntityDefaultValuesSourceType,
  EntityFormMeta,
  EntityFormMode,
  EntityInputFormInfo,
  EntityMeta,
} from "../ts/entity";
import { ArrayHelper } from "./array";
import { IdHelper } from "./id";
import { ObjectHelper } from "./object";
import { StringHelper } from "./string";

export class EntityHelper {
  /** Returns meta about entity forms (see @type EntityFormType). */
  public static getEntityFormMeta(entity: BaseEntityDtoAny): EntityFormMeta {
    const entity2 = entity as BaseEntityDtoAll;
    return {
      formType: entity2.formType,
      entity: entity2.formType === EntityFormType.Entity ? (entity2 as BaseEntityDto) : null,
      entityRef: entity2.formType === EntityFormType.Ref ? (entity2 as BaseEntityRefDto) : null,
      entityFullRef:
        entity2.formType === EntityFormType.FullRef ? (entity2 as BaseEntityFullRefDto) : null,
      entityData: entity2.formType === EntityFormType.Data ? (entity2 as BaseEntityDataDto) : null,
      isEntity: entity2.formType === EntityFormType.Entity,
      isRef: entity2.formType === EntityFormType.Ref,
      isFullRef: entity2.formType === EntityFormType.FullRef,
      isData: entity2.formType === EntityFormType.Data,
      dataMeta: this.getEntityDataMeta(entity),
    };
  }

  /** Returns meta about entity forms (see @type EntityFormType). */
  public static getEntityFormMetaOrNull(entity: Nil<BaseEntityDtoAny>): EntityFormMeta | null {
    if (!entity) {
      return null;
    }
    return this.getEntityFormMeta(entity);
  }

  /** Returns meta about entity data (see @type EntityFormType.Data). */
  public static getEntityDataMeta(entityData: Nil<BaseEntityDataDtoAny>): EntityDataMeta | null {
    const entityData2 = entityData as Nil<BaseEntityDataDtoAll>;
    if (!entityData2 || entityData2.formType !== EntityFormType.Data) {
      return null;
    }
    return {
      ...entityData2,
      isRef: entityData2.dataOriginMode === EntityDataOriginMode.Ref,
      isInline: entityData2.dataOriginMode === EntityDataOriginMode.Inline,
    };
  }

  public static getEntityMeta(
    entity: Nil<BaseEntityDtoAny>,
    currentTenant?: TenantDto | null,
  ): EntityMeta | null {
    if (!entity) {
      return null;
    }

    const currentTenantComputed = currentTenant || store.getState().tenants.currentTenant;

    const formMeta = this.getEntityFormMeta(entity);

    const mightHaveTenantDto = entity as IMightHaveTenantDto;
    const hasGlobalNumber = entity as IHasGlobalNumberDto;
    const hasLocalNumber = entity as IHasLocalNumberDto;
    const imported = entity as IMightHaveImportMetaDto;
    const mightHaveDataGrants = entity as IMightHaveDataGrantsDto | IMightHaveDataGrantsBriefDto;
    const mightHaveTenantRequests = entity as IMightHaveTenantRequestsDto;
    const assetSourceEntity = entity as IAssetSourceEntityDto;
    const mightHaveDepartments = entity as IMightHaveDepartmentsDto;
    const mightHaveLocations = entity as IMightHaveLocationsDto;
    const mightHavePools = entity as IMightHavePoolsDto;
    const mightHaveTags = entity as IMightHaveTagsDto;
    const mightHaveUnseenInfo = entity as IMightHaveUnseenInfoDto;
    const mightHaveOrigin = entity as IMightHaveOriginDto;

    // directly assign each property for clarity of what's included in the meta
    return {
      formMeta: formMeta,
      id: entity.id || undefined,
      tenantId: mightHaveTenantDto.tenantId,
      globalNumber: hasGlobalNumber.globalNumber,
      localNumber: hasLocalNumber.localNumber,
      externalNumber: (entity as { externalNumber: string | undefined }).externalNumber,
      importMeta: imported.importMeta,
      grantsMeta: mightHaveDataGrants.grantsMeta
        ? {
            ...mightHaveDataGrants.grantsMeta,
            isIssuedByMe:
              !!currentTenantComputed?.id &&
              currentTenantComputed.id === mightHaveDataGrants.grantsMeta?.issuerTenantId,
            isConsumedByMe:
              !!currentTenantComputed?.id &&
              !!mightHaveDataGrants.grantsMeta?.consumerTenantIdsMap &&
              mightHaveDataGrants.grantsMeta.consumerTenantIdsMap[currentTenantComputed.id],
          }
        : undefined,
      tenantRequestsMeta: mightHaveTenantRequests.tenantRequestsMeta,
      assetMeta: assetSourceEntity.assetMeta,
      poolsMeta: mightHavePools.poolsMeta,
      departmentIds: mightHaveDepartments.departmentIds,
      locationIds: mightHaveLocations.locationIds,
      tags: mightHaveTags.tags,
      myUnseen: mightHaveUnseenInfo.myUnseen,
      originType: mightHaveOrigin.originType,

      // TODO: remove temporal when Dtos will have departmentIds
      //department: mightHaveDepartments.department,
      // TODO: remove temporal when Dtos will have locationIds
      //location: mightHaveLocations.location,
    };
  }

  public static getAffiliationInfo(entity: Nil<BaseEntityDtoAny>): EntityAffiliationInfo | null {
    if (!entity) {
      return null;
    }

    const mightHaveTenantDto = entity as IMightHaveTenantDto;
    const mightHaveDepartments = entity as IMightHaveDepartmentsDto;
    const mightHaveLocations = entity as IMightHaveLocationsDto;

    return {
      tenantId: mightHaveTenantDto.tenantId,
      departmentIds: mightHaveDepartments.departmentIds,
      locationIds: mightHaveLocations.locationIds,
    };
  }

  public static filterEntitiesByIds<TEntity extends BaseEntityDto>(
    entities: TEntity[],
    ids: string[],
  ): TEntity[] {
    return ids
      .map((id) => entities.find((x) => x.id === id))
      .filter((x) => !isEmpty(x)) as TEntity[];
  }

  public static isEntity<TEntity extends BaseEntityDtoAny>(entity: Nil<TEntity>): boolean {
    return entity?.formType === EntityFormType.Entity;
  }

  public static isFull<TEntity extends BaseEntityDtoAny>(entity: Nil<TEntity>): boolean {
    return (
      entity?.formType === EntityFormType.Entity || entity?.formType === EntityFormType.FullRef
    );
  }

  public static nullIfNotEntity<TEntity extends BaseEntityDtoAny>(
    entity: Nil<TEntity>,
  ): TEntity | null {
    return this.isEntity(entity) ? entity || null : null;
  }

  public static nullIfNotFull<TEntity extends BaseEntityDtoAny>(
    entity: Nil<TEntity>,
  ): TEntity | null {
    return this.isFull(entity) ? entity || null : null;
  }

  // #region Entity form

  public static getEntityInputFormInfoByEntityId(entityId: Nil<string>): EntityInputFormInfo {
    const mode = !entityId ? EntityFormMode.Create : EntityFormMode.Update;
    return {
      entityId: entityId,
      mode: mode,
      isCreate: mode === EntityFormMode.Create,
      isUpdate: mode === EntityFormMode.Update,
    };
  }

  /** Checks if default values are empty (i.e. contains no real data, ignoring meta). */
  public static isDefaultValuesEmpty<T extends BaseEntityDefaultValues>(
    defaultValues: Nil<T>,
  ): boolean {
    const cleanedDefaultValues = this.cleanDefaultValues(defaultValues);
    return !cleanedDefaultValues || ObjectHelper.isEmpty(cleanedDefaultValues);
  }

  /** Returns default values without extra meta or internal fields. */
  public static cleanDefaultValues<T extends BaseEntityDefaultValues>(
    defaultValues: Nil<T>,
  ): T | null {
    if (!defaultValues) {
      return null;
    }
    const newDefaultValues = { ...defaultValues };
    delete newDefaultValues.meta;
    return newDefaultValues;
  }

  public static computeDefaultValuesMatchStatus<
    TDefaultValues extends BaseEntityDefaultValues,
  >(params: {
    defaultValues: Nil<TDefaultValues>;
    selectedEntityId: Nil<string>;
  }): EntityDefaultValuesMatchStatus | null {
    const isExtraction =
      params.defaultValues?.meta?.sourceType === EntityDefaultValuesSourceType.Extraction;
    const isDefaultValuesEmpty = EntityHelper.isDefaultValuesEmpty(params.defaultValues);
    const matchedId = params.defaultValues?.meta?.matchedId;

    return isExtraction && !!matchedId && params.selectedEntityId === matchedId
      ? EntityDefaultValuesMatchStatus.Matched
      : isExtraction && !matchedId && !isDefaultValuesEmpty
        ? EntityDefaultValuesMatchStatus.Prefilled
        : isExtraction && !params.selectedEntityId
          ? EntityDefaultValuesMatchStatus.NoMatch
          : null;
  }

  public static computeDefaultValuesMatchStatusForAutocomplete(params: {
    defaultValuesMeta: Nil<EntityDefaultValuesMeta>;
    selectedEntityId: Nil<string>;
  }): EntityDefaultValuesMatchStatus | null {
    // return only relevant statuses for autocomplete
    const allowedStatuses: EntityDefaultValuesMatchStatus[] = [
      EntityDefaultValuesMatchStatus.Matched,
      EntityDefaultValuesMatchStatus.NoMatch,
    ];
    const status = this.computeDefaultValuesMatchStatus({
      defaultValues: {
        meta: params.defaultValuesMeta,
      },
      selectedEntityId: params.selectedEntityId,
    });
    return status && allowedStatuses.includes(status) ? status : null;
  }

  // #endregion Entity form

  // #region EntityData

  public static isEntityDataInMode(
    entityData: Nil<BaseEntityDataDto>,
    dataOriginMode: EntityDataOriginMode,
  ): boolean {
    return !!entityData && entityData.dataOriginMode === dataOriginMode;
  }

  public static isEntityDataInRefMode(entityData: Nil<BaseEntityDataDto>): boolean {
    return this.isEntityDataInMode(entityData, EntityDataOriginMode.Ref);
  }

  public static isEntityDataInInlineMode(entityData: Nil<BaseEntityDataDto>): boolean {
    return this.isEntityDataInMode(entityData, EntityDataOriginMode.Inline);
  }

  public static switchEntityDataToRefMode<TEntityData extends BaseEntityDataDto>(
    entityData: TEntityData,
    entity: BaseEntityDto | null,
  ): TEntityData {
    return {
      ...entityData,
      formType: EntityFormType.Data,
      dataOriginMode: EntityDataOriginMode.Ref,
      id:
        IdHelper.nullIfTempFeId(entity?.id) ||
        IdHelper.nullIfTempFeId(entityData.originalId) ||
        undefined,
      originalId: undefined,
      originalIds: undefined,
      originalTenantId: undefined,
    };
  }

  public static switchEntityDataToInlineMode<TEntityData extends BaseEntityDataDto>(
    entityData: TEntityData,
  ): TEntityData {
    const entityMeta = this.getEntityMeta(entityData);

    return {
      ...entityData,
      formType: EntityFormType.Data,
      dataOriginMode: EntityDataOriginMode.Inline,
      // NB: make sure id is always set as it's required for FE logic, reset it before sending to BE.
      id: this.isEntityDataInInlineMode(entityData)
        ? entityData.id || IdHelper.newTempFeId()
        : IdHelper.newTempFeId(),
      // ensure valid entity id
      originalId:
        IdHelper.nullIfTempFeId(entityData.id) ||
        IdHelper.nullIfTempFeId(entityData.originalId) ||
        undefined,
      originalIds: ArrayHelper.undefinedIfEmpty(entityData?.originalIds),
      originalTenantId: entityMeta?.tenantId || entityData.originalTenantId,
    };
  }

  public static newBaseEntityDataInInlineMode(): BaseEntityDataDto {
    return {
      formType: EntityFormType.Data,
      dataOriginMode: EntityDataOriginMode.Inline,
      id: IdHelper.newTempFeId(),
      originalId: undefined,
      originalIds: undefined,
      originalTenantId: undefined,
    };
  }

  // #endregion EntityData

  // #region Display

  public static getNumberTitle(data?: {
    localNumber?: string | null;
    globalNumber?: string | null;
    externalNumber?: string | null;
  }): string {
    return (
      StringHelper.join([data?.externalNumber, data?.localNumber || data?.globalNumber], " / ", {
        skipEmpty: true,
      }) || ""
    );
  }

  // #endregion Display
}
